import React, { useState, useEffect } from 'react'
import { RefreshCw, FileText, BarChart, AlertCircle } from 'lucide-react'
import { Button } from '../components/ui/Button'
import { AdminLayout } from '../components/admin/AdminLayout'
import { ProfilesList } from '../components/profiles/ProfilesList'
import { ProfilesFilters } from '../components/profiles/ProfilesFilters'
import { ProfilesStats } from '../components/profiles/ProfilesStats'
import { ProfilesService } from '../services/profilesService'
import type { 
  IntelligenceProfile, 
  ProfileStatusFilter, 
  ProfilesQuery,
  ProfilesCounts,
  ProfilesPagination,
  ReportAsset 
} from '../types'

const DEFAULT_PAGE_SIZE = 20

export function AdminProfilesPage() {
  const [profiles, setProfiles] = useState<IntelligenceProfile[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [executingOrchestrator, setExecutingOrchestrator] = useState<string | null>(null)
  // Removed: justExecutedProfiles, statusPolling, progressStartTimes, completingProfiles, fadingOutProfiles
  // These were all for complex real-time progress tracking that we no longer need
  
  // Query state
  const [currentPage, setCurrentPage] = useState(1)
  const [activeTab, setActiveTab] = useState<ProfileStatusFilter>('all')
  const [tierFilter, setTierFilter] = useState<string>('all')
  const [billingFilter, setBillingFilter] = useState<string>('all')
  const [counts, setCounts] = useState<ProfilesCounts>({
    total: 0,
    pending: 0,
    processed: 0,
    assigned: 0
  })
  const [pagination, setPagination] = useState<ProfilesPagination>({
    page: 1,
    limit: DEFAULT_PAGE_SIZE,
    total: 0,
    totalPages: 0,
    hasNext: false,
    hasPrev: false
  })

  useEffect(() => {
    loadProfiles()
  }, [currentPage, activeTab, tierFilter, billingFilter])

  // Removed: Complex polling cleanup useEffect - no longer needed with background processing

  const loadProfiles = async () => {
    try {
      setLoading(true)
      setError(null)
      
      // Check if user is authenticated
      const token = localStorage.getItem('stratiqx_access_token')
      if (!token) {
        setError('Authentication required. Please log in to access admin profiles.')
        setLoading(false)
        return
      }
      
      const query: ProfilesQuery = {
        page: currentPage,
        limit: DEFAULT_PAGE_SIZE,
        status: activeTab,
        sortBy: 'created_at',
        sortOrder: 'desc'
      }
      
      const response = await ProfilesService.fetchProfiles(query)
      
      if (response.success) {
        // Simplified: Just fetch assets for completed profiles, no complex progress tracking
        const profilesWithAssets = await Promise.all(
          response.profiles.map(async (profile) => {
            const simpleProfile: IntelligenceProfile = { ...profile }
            
            // Simplified: Only fetch assets for completed profiles, no complex progress tracking
            if (profile.submission?.processing_status === 'completed' ||
                (profile.submission?.processing_status === 'failed' && profile.submission?.response_url)) {
              try {
                const statusResult = await ProfilesService.getStatus(profile.tracking_id)
                if (statusResult.success) {
                  const assets = statusResult.assets || statusResult.data?.assets || []
                  simpleProfile.submission = profile.submission ? {
                    ...profile.submission,
                    assets: assets
                  } : null
                }
              } catch (error) {
                console.error(`Failed to fetch assets for ${profile.tracking_id}:`, error)
              }
            }

            return simpleProfile
          })
        )
        
        // Apply client-side filtering for tier level and billing type
        let filteredProfiles = profilesWithAssets
        
        if (tierFilter !== 'all') {
          filteredProfiles = filteredProfiles.filter(profile => 
            profile.tier_level === tierFilter
          )
        }
        
        if (billingFilter !== 'all') {
          filteredProfiles = filteredProfiles.filter(profile => 
            profile.billing_type === billingFilter
          )
        }
        
        setProfiles(filteredProfiles)
        setPagination(response.pagination)
        setCounts(response.counts)
      } else {
        setError(response.error || 'Failed to load profiles')
      }
    } catch (err) {
      console.error('Error loading profiles:', err)
      
      // Check if it's an authentication error
      if (err instanceof Error && err.message.includes('401')) {
        setError('Authentication expired. Please log in again to access admin profiles.')
        // Optionally clear the expired token
        localStorage.removeItem('stratiqx_access_token')
      } else {
        setError('Failed to fetch profiles. Please check your connection and try again.')
      }
    } finally {
      setLoading(false)
    }
  }

  const handleTabChange = (newTab: string) => {
    setActiveTab(newTab as ProfileStatusFilter)
    setCurrentPage(1) // Reset to first page when changing tabs
  }

  const handleTierFilterChange = (newTier: string) => {
    setTierFilter(newTier)
    setCurrentPage(1) // Reset to first page when changing filters
  }

  const handleBillingFilterChange = (newBilling: string) => {
    setBillingFilter(newBilling)
    setCurrentPage(1) // Reset to first page when changing filters
  }

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  // Removed: Complex status polling function - no longer needed with background processing

  const handleReprocessAnalysis = async (profile: IntelligenceProfile) => {
    // Re-queue a completed/failed analysis for background processing
    if (executingOrchestrator) {
      return
    }

    try {
      setExecutingOrchestrator(profile.id)

      // Reset the status to 'pending' so background job picks it up
      const result = await ProfilesService.reprocessAnalysis(profile.id, profile.tracking_id)

      if (result.success) {
        console.log('Analysis re-queued successfully:', result.message)
        // Refresh profiles to show updated status
        await loadProfiles()
      } else {
        console.error('Failed to re-queue analysis:', result.message)
      }
    } catch (err) {
      console.error('Error re-queuing analysis:', err)
    } finally {
      setExecutingOrchestrator(null)
    }
  }

  const handleExecuteOrchestrator = async (profile: IntelligenceProfile) => {
    // Simplified: Just queue the analysis, no complex progress tracking
    if (executingOrchestrator) {
      return
    }

    try {
      setExecutingOrchestrator(profile.id)

      // Execute orchestrator - now just queues the analysis
      const result = await ProfilesService.executeOrchestrator(profile.id, profile.tracking_id)

      if (result.success) {
        console.log('Analysis queued successfully:', result.message)
        // Refresh profiles to show updated status
        await loadProfiles()
      } else {
        console.error('Failed to queue analysis:', result.message)
      }
    } catch (err) {
      console.error('Error queuing analysis:', err)
    } finally {
      setExecutingOrchestrator(null)
    }
  }

  const handleDownloadAsset = async (asset: ReportAsset) => {
    try {
      // Make authenticated request to download the actual file content
      const response = await ProfilesService.downloadAssetContent(asset.id)
      
      if (response.ok) {
        // Get the file blob from the response
        const blob = await response.blob()
        
        // Create object URL and trigger download
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = asset.file_name || `asset-${asset.id}.${asset.asset_type}`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        
        // Clean up the object URL
        window.URL.revokeObjectURL(url)
        
        console.log(`✅ Successfully downloaded asset: ${asset.file_name}`)
      } else {
        const errorData = await response.json().catch(() => ({ error: 'Download failed' }))
        console.error('Download failed:', errorData)
        alert(`Download failed: ${errorData.error || 'Unknown error'}`)
      }
    } catch (error) {
      console.error('Error downloading asset:', error)
      alert(`Download error: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  // Removed: toggleWorkflowDetails - no longer needed without complex progress UI

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getStatusVariant = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return 'default'
      case 'in_progress':
        return 'secondary'
      case 'draft':
        return 'outline'
      default:
        return 'secondary'
    }
  }

  const getSubmissionStatusVariant = (status?: string) => {
    switch (status?.toLowerCase()) {
      case 'submitted':
        return 'outline'
      case 'processing':
        return 'secondary'
      case 'processed':
      case 'completed':
        return 'default'
      case 'failed':
        return 'destructive'
      default:
        return 'outline'
    }
  }



  if (loading && profiles.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-center h-64">
            <RefreshCw className="h-8 w-8 animate-spin text-blue-600" />
            <span className="ml-2 text-gray-600">Loading intelligence profiles...</span>
          </div>
        </div>
      </div>
    )
  }

  if (error && profiles.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-center h-64">
            <AlertCircle className="h-8 w-8 text-red-600" />
            <div className="ml-2">
              <p className="text-red-600 font-medium">Error loading profiles</p>
              <p className="text-gray-600 text-sm">{error}</p>
              <Button 
                onClick={loadProfiles} 
                variant="outline" 
                className="mt-2"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Retry
              </Button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <AdminLayout 
      title="User Profiles"
      subtitle="Manage and execute onboarding profiles for orchestrator processing"
    >
      {/* Refresh Controls */}
      <div className="flex justify-end mb-6">
        <Button 
          onClick={loadProfiles} 
          variant="outline"
          disabled={loading}
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

        {/* Summary Stats */}
        <ProfilesStats counts={counts} className="mb-8" />

        {/* Filters */}
        <ProfilesFilters
          activeTab={activeTab}
          tierFilter={tierFilter}
          billingFilter={billingFilter}
          counts={counts}
          onTabChange={handleTabChange}
          onTierFilterChange={handleTierFilterChange}
          onBillingFilterChange={handleBillingFilterChange}
          onClearFilters={() => {
            setTierFilter('all')
            setBillingFilter('all')
          }}
        />

        {/* Profiles List */}
        <ProfilesList
          profiles={profiles}
          activeTab={activeTab}
          pagination={pagination}
          executingOrchestrator={executingOrchestrator}
          onExecuteOrchestrator={handleExecuteOrchestrator}
          onReprocessAnalysis={handleReprocessAnalysis}
          onPageChange={handlePageChange}
        />
    </AdminLayout>
  )
}